import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import ReviewProofStrip from "@/components/ReviewProofStrip";
import CaseStudyProofLinks from "@/components/CaseStudyProofLinks";
import { 
  Scale, MapPin, Star, TrendingUp, Users, Target, 
  CheckCircle, ArrowRight, Phone, Calendar, Gavel,
  FileText, Shield, BarChart3, Award, Download
} from "lucide-react";

const LawFirmMarketingPage = () => {

  useEffect(() => {
    document.title = "Law Firm Marketing Dallas | Attorney SEO | Digital Media Ninja";
  }, []);

  const stats = [
    { value: "Local SEO", label: "Map Pack + Organic", icon: TrendingUp },
    { value: "Intake", label: "Call & Consult Tracking", icon: Scale },
    { value: "Content", label: "Practice-Area Pages", icon: Target },
    { value: "Ethics", label: "Bar-Compliant Marketing", icon: BarChart3 }
  ];

  const services = [
    { icon: MapPin, title: "Local SEO for Attorneys", desc: "Rank #1 for practice area searches like 'personal injury lawyer Dallas' and 'divorce attorney Fort Worth.'" },
    { icon: FileText, title: "Legal Content Marketing", desc: "Authoritative blog content and practice area pages that establish expertise and attract cases." },
    { icon: Star, title: "Attorney Reputation Management", desc: "Build a 5-star reputation on Google, Avvo, and legal directories." },
    { icon: Award, title: "Legal Directory Optimization", desc: "Optimize Avvo, FindLaw, Justia, and Super Lawyers profiles for maximum visibility." },
    { icon: Shield, title: "Ethical PPC Advertising", desc: "Compliant Google Ads campaigns that generate qualified case inquiries." },
    { icon: BarChart3, title: "Case Value Tracking", desc: "Track ROI from marketing investment to signed cases with full attribution." }
  ];

  const practiceAreas = [
    "Personal Injury", "Family Law", "Criminal Defense", "Estate Planning",
    "Business Law", "Immigration", "Employment Law", "Real Estate Law"
  ];

  const serviceAreas = [
    "Dallas", "Fort Worth", "Plano", "Arlington", "Irving",
    "Frisco", "McKinney", "Denton", "Richardson"
  ];

  const faqs = [
    { 
      q: "Is attorney advertising regulated in Texas?", 
      a: "Yes, attorney advertising must comply with the Texas Disciplinary Rules of Professional Conduct. We ensure all marketing materials meet ethical guidelines, including proper disclaimers, truthful representations, and avoiding misleading claims. Our team stays current with State Bar rules to keep your marketing compliant while still being effective." 
    },
    { 
      q: "What keywords should law firms target?", 
      a: "Law firms should target practice area + location keywords (e.g., 'personal injury lawyer Dallas', 'divorce attorney Fort Worth') as well as question-based queries potential clients search. We conduct comprehensive keyword research to identify high-intent searches with strong case potential, focusing on keywords that attract qualified clients, not just traffic." 
    },
    { 
      q: "How long does law firm SEO take to show results?", 
      a: "Legal SEO is highly competitive. Most law firms see meaningful ranking improvements in 4-6 months, with significant lead generation increases by month 6-12. PPC advertising can generate leads immediately while SEO builds. We provide monthly reporting to track progress and adjust strategy as needed." 
    },
    { 
      q: "How much should a law firm spend on marketing?", 
      a: "Marketing budget varies by practice area and competition level. Most successful Dallas law firms invest 5-10% of gross revenue in marketing. For new or growing firms, we recommend starting with $3,000-8,000/month to build a strong foundation. The key is tracking ROI — we measure every call and consultation so you know exactly what your marketing produces." 
    }
  ];

  const caseStudyResults = [
    { metric: "Monthly Case Inquiries", before: "12", after: "45", change: "+275%" },
    { metric: "Avg Case Value", before: "$8,500", after: "$14,200", change: "+67%" },
    { metric: "Google Maps Ranking", before: "#12", after: "#2", change: "Page 1" }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Law Firm Marketing & Attorney SEO Services Dallas",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Digital Media Ninja",
          "address": { "@type": "PostalAddress", "addressLocality": "Dallas", "addressRegion": "TX", "addressCountry": "US" },
          "telephone": "(432) 934-9470"
        },
        "areaServed": serviceAreas.map(city => ({ "@type": "City", "name": city })),
        "description": "Ethical, effective digital marketing for attorneys and law firms in Dallas-Fort Worth. Attorney SEO, legal content marketing, and reputation management.",
        "serviceType": "Legal Marketing Services"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-black" data-testid="law-firm-marketing-page">
      <Helmet>
        <title>Law Firm Marketing Dallas | Attorney SEO | Digital Media Ninja</title>
        <meta name="description" content="Ethical, effective legal marketing and SEO services for Dallas law firms. Generate more qualified case inquiries with attorney SEO, legal content marketing, and reputation management." />
        <meta name="keywords" content="law firm marketing Dallas, attorney SEO, legal marketing DFW, lawyer advertising Dallas, law firm SEO, attorney marketing Texas, legal lead generation" />
        <link rel="canonical" href="https://digitalmedianinja.com/law-firm-marketing" />
        <meta property="og:title" content="Law Firm Marketing Dallas | Attorney SEO" />
        <meta property="og:description" content="Ethical legal marketing for Dallas attorneys. SEO, PPC, reputation management." />
        <meta property="og:url" content="https://digitalmedianinja.com/law-firm-marketing" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-600/10 via-transparent to-yellow-500/10"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-6">
              <Scale className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">Legal Marketing Specialists</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Get More <span className="text-amber-400">Qualified Legal Clients</span> in Dallas
            </h1>
            
            <p className="text-lg text-gray-300 mb-8 max-w-2xl">
              Ethical, effective marketing for Dallas attorneys. We help law firms generate more case inquiries while maintaining professional standards.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-12">
              <BookConsultationButton meetingType="lawFirm" size="lg">
                Get Free Law Firm Marketing Audit <ArrowRight className="w-5 h-5 ml-2" />
              </BookConsultationButton>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-amber-400/30 text-amber-400 hover:bg-amber-400/10"
                onClick={() => window.location.href='/blog/how-to-market-a-law-firm'}
                data-testid="read-blog-btn"
              >
                <FileText className="w-5 h-5 mr-2" /> Read Our Full Guide
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => window.location.href='/guides/law-firm-marketing'}
                data-testid="read-playbook-btn"
              >
                <Download className="w-5 h-5 mr-2" /> Marketing Playbook
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                  <stat.icon className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Law Firm Marketing Services</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">Comprehensive legal marketing designed to generate qualified cases while maintaining ethical standards.</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <Card key={i} className="bg-[#111] border-white/10 hover:border-amber-400/30 transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-amber-400/10 flex items-center justify-center mb-4">
                    <svc.icon className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{svc.title}</h3>
                  <p className="text-gray-400 text-sm">{svc.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Practice Areas We Market</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {practiceAreas.map((area, i) => (
              <div key={i} className="p-4 bg-white/5 rounded-xl border border-white/10 text-center hover:border-amber-400/30 transition-colors">
                <p className="text-white font-medium">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">DFW Markets We Serve</h2>
          <p className="text-gray-400 text-center mb-12">We help law firms dominate search in every Dallas-Fort Worth market.</p>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
            {serviceAreas.map((area, i) => (
              <div key={i} className="p-3 bg-white/5 rounded-xl border border-white/10 text-center hover:border-amber-400/30 transition-colors">
                <p className="text-white font-medium text-sm">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Results */}
      <section className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Real Law Firm Marketing Results</h2>
          <p className="text-gray-400 text-center mb-12">See how we helped a Dallas personal injury firm grow their practice.</p>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {caseStudyResults.map((result, i) => (
              <div key={i} className="text-center p-8 bg-[#111] rounded-2xl border border-white/10">
                <p className="text-gray-400 mb-2">{result.metric}</p>
                <div className="flex items-center justify-center gap-4 mb-2">
                  <span className="text-gray-500 line-through">{result.before}</span>
                  <ArrowRight className="w-4 h-4 text-amber-400" />
                  <span className="text-2xl font-bold text-white">{result.after}</span>
                </div>
                <p className="text-amber-400 font-semibold">{result.change}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CaseStudyProofLinks slugs={["roblesfamilylaw"]} heading="A law firm site we built that gets found" />

      <ReviewProofStrip />

      {/* FAQ */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Law Firm Marketing FAQs</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="group bg-[#111] rounded-xl border border-white/10">
                <summary className="p-6 cursor-pointer text-white font-medium flex items-center justify-between hover:bg-white/5">
                  {faq.q}
                  <span className="text-amber-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-6 pb-6 text-gray-400">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-amber-500/10 to-yellow-500/10">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Grow Your Law Practice?</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">Get a free law firm marketing audit and custom strategy to generate more qualified cases.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <BookConsultationButton meetingType="lawFirm" size="lg">
              <Calendar className="w-5 h-5 mr-2" /> Book Strategy Call
            </BookConsultationButton>
            <a href="tel:+14329349470">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Call (432) 934-9470
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LawFirmMarketingPage;
